import { useQuery } from "@tanstack/react-query";
import { reviewRecognitionKeys } from "@/lib/query";

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

/** `[]` means every cycle — "All time" — same convention as the competency report. */
function cycleParam(cycleIds: string[]): string {
  return cycleIds.length > 0 ? `?cycleIds=${cycleIds.join(",")}` : "";
}

export interface CycleEndorsementRow {
  id: string;
  cycleId: string;
  cycleName: string;
  subjectPersonId: string;
  subjectName: string;
  tenetTitle: string;
  detail: string;
  endorserName: string;
  relationshipKey: string;
  relationshipName: string;
}

/** WI-278 — every endorsement across the selected cycles, with who gave it and why. */
export function useCycleEndorsements(cycleIds: string[]) {
  return useQuery({
    queryKey: reviewRecognitionKeys.endorsements(cycleIds),
    queryFn: async (): Promise<CycleEndorsementRow[]> => {
      const res = await fetch(`/api/reports/recognition/endorsements${cycleParam(cycleIds)}`);
      if (!res.ok) throw await failure(res, "Failed to load endorsements");
      return (await res.json()).data;
    },
  });
}

export interface EndorsementRankingRow {
  subjectPersonId: string;
  subjectName: string;
  totalEndorsements: number;
  byTenet: { tenetId: string; tenetTitle: string; count: number }[];
}

/** WI-279 — people ranked by endorsements received, by tenet. */
export function useEndorsementRankings(cycleIds: string[]) {
  return useQuery({
    queryKey: reviewRecognitionKeys.rankings(cycleIds),
    queryFn: async (): Promise<EndorsementRankingRow[]> => {
      const res = await fetch(
        `/api/reports/recognition/endorsement-rankings${cycleParam(cycleIds)}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load endorsement rankings");
      return (await res.json()).data;
    },
  });
}

export interface CriterionTopPerformerRow {
  subjectPersonId: string;
  subjectName: string;
  averageOrdinal: number;
}

export interface CriterionScoreGroup {
  questionTitle: string;
  categoryName: string | null;
  categoryColor: string | null;
  topPerformers: CriterionTopPerformerRow[];
}

/** WI-280 — top performers by question score, across the selected cycles. */
export function useTopPerformersByCriterion(cycleIds: string[]) {
  return useQuery({
    queryKey: reviewRecognitionKeys.topPerformers(cycleIds),
    queryFn: async (): Promise<CriterionScoreGroup[]> => {
      const res = await fetch(`/api/reports/recognition/top-performers${cycleParam(cycleIds)}`);
      if (!res.ok) throw await failure(res, "Failed to load top performers");
      return (await res.json()).data;
    },
  });
}
